// Centralized design tokens with light + dark themes.
//
// The app uses an inline-style architecture (style={...}) where components
// import these objects directly. MANY components build their `styles` object at
// MODULE scope, which means the values are read ONCE at import time and frozen.
// To make a runtime light/dark toggle work even for those frozen objects, every
// theme-dependent token below resolves to a LIVE CSS custom property
// (`var(--x)`). index.css defines each variable for dark (`:root`) and light
// (`:root[data-theme='light']`), and ThemeContext flips `data-theme` on <html>.
// Because the browser resolves `var(--x)` at paint time, even a frozen inline
// style like `{ color: 'var(--text)' }` updates the instant the theme changes.
//
// Brand + status colours are theme-INDEPENDENT (identical in both themes) and
// stay as real hex values so they can be consumed by canvas / Leaflet / JS that
// cannot resolve CSS variables.

// ── Brand + status colours (shared by both themes, real values) ─────────────
const BRAND = {
  parGreen: '#2fb65c',
  parGreenBright: '#34d36b',
  parLime: '#91C83E',
  parGreenDeep: '#1d8c3a',
  red: '#e5544b',
  redDeep: '#b53a32',
  amber: '#f0a830',
  blue: '#3a9bd4',
  silver: '#C0C0C0',
  gold: '#91C83E',
  platinum: '#2fb65c',
  rubber: '#dc2626',
  white: '#ffffff',
  black: '#000000',
  textOnAccent: '#ffffff'
};

// ── Theme-dependent tokens → live CSS variables (switch via [data-theme]) ────
const THEME_VARS = {
  bg: 'var(--bg)',
  bgElevated: 'var(--bg-elevated)',
  ink: 'var(--ink)',

  text: 'var(--text)',
  textMuted: 'var(--text-muted)',
  textFaint: 'var(--text-faint)',

  glass: 'var(--glass)',
  glassStrong: 'var(--glass-strong)',
  glassBorder: 'var(--glass-border)',
  glassBorderStrong: 'var(--glass-border-strong)',

  surface: 'var(--surface)',
  surfaceSoft: 'var(--surface-soft)',
  surface2: 'var(--surface-2)',
  surfaceSolid: 'var(--surface-solid)',
  surfaceDeep: 'var(--surface-deep)',
  inputBg: 'var(--input-bg)',
  border: 'var(--border)',
  borderSoft: 'var(--border-soft)',
  borderStrong: 'var(--border-strong)',

  navBg: 'var(--nav-bg)',
  menuBg: 'var(--menu-bg)',
  overlay: 'var(--overlay)',
  pill: 'var(--pill)',
  greenSoft: 'var(--green-soft)',
  redSoft: 'var(--red-soft)',
  amberSoft: 'var(--amber-soft)',
  blueSoft: 'var(--blue-soft)'
};

// ── Live exports ────────────────────────────────────────────────────────────
export const colors = { ...BRAND, ...THEME_VARS };

export const gradients = {
  // Button gradient is brand-green in both themes.
  button: 'linear-gradient(135deg, #34d36b 0%, #2fb65c 55%, #1d8c3a 100%)',
  buttonHover: 'linear-gradient(135deg, #3ee07a 0%, #34c466 55%, #229a44 100%)',
  brand: 'var(--gradient-brand)',
  bgBase: 'var(--gradient-bg-base)'
};

// Per-route background gradients. PageTransition cross-fades between these.
export const bgVariants = {
  default: 'var(--bg-grad-default)',
  home: 'var(--bg-grad-home)',
  find: 'var(--bg-grad-find)',
  add: 'var(--bg-grad-add)',
  clients: 'var(--bg-grad-clients)',
  inbox: 'var(--bg-grad-inbox)',
  profile: 'var(--bg-grad-profile)',
  nfc: 'var(--bg-grad-nfc)',
  admin: 'var(--bg-grad-admin)',
  tasks: 'var(--bg-grad-tasks)',
  login: 'var(--bg-grad-login)',
  landing: 'var(--bg-grad-landing)'
};

export const shadows = {
  sm: 'var(--shadow-sm)',
  md: 'var(--shadow-md)',
  lg: 'var(--shadow-lg)',
  glow: 'var(--shadow-glow)'
};

export const radii = {
  sm: '8px',
  md: '14px',
  lg: '20px',
  xl: '28px',
  pill: '999px'
};

export const spacing = {
  xs: '6px',
  sm: '10px',
  md: '16px',
  lg: '24px',
  xl: '36px',
  xxl: '56px'
};

// Reusable glass-surface style objects for cards/panels. All values are CSS
// variables, so these flip with the theme even though they are created once.
export const glass = {
  background: colors.glass,
  border: `1px solid ${colors.glassBorder}`,
  borderRadius: radii.lg,
  boxShadow: shadows.md,
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  color: colors.text
};

export const glassStrong = {
  ...glass,
  background: colors.glassStrong,
  border: `1px solid ${colors.glassBorderStrong}`
};

// Theme is switched purely via the `data-theme` attribute on <html> (see
// ThemeContext + index.css). The token objects already hold `var(--x)` strings
// that are identical in both modes, so there is nothing to mutate here; we just
// normalise + return the requested mode.
export function applyTheme(mode) {
  return mode === 'light' ? 'light' : 'dark';
}

export const THEME_STORAGE_KEY = 'par-theme';

const theme = { colors, gradients, bgVariants, radii, shadows, spacing, glass, glassStrong, applyTheme };
export default theme;
